/**
 * Google Analytics 4 wrapper.
 *
 * Design goals:
 *   - Load and send events ONLY on the live consumer host so preview,
 *     dev, and the beta environment never pollute real dashboards.
 *     `beta.neighborli.net` stays in the set until DNS cutover, because
 *     that host is still the live app on Emergent.
 *   - Zero PII: user_id is a stable SHA-256-lite hash of the raw id.
 *   - Safe to import from anywhere — every helper is a no-op when GA is
 *     disabled (SSR-safe, no window access unless gated).
 *
 * Usage:
 *   import * as analytics from './services/analytics';
 *   analytics.initGA();                     // once, at app boot
 *   analytics.identify(user.id);            // after login
 *   analytics.clearIdentity();              // on logout
 *   analytics.trackPageView('/items');      // on every route change
 *   analytics.track('borrow_requested', {}) // domain events
 */
const MEASUREMENT_ID = 'G-742RYFFJM6';
const PROD_HOSTNAMES = new Set([
  'beta.neighborli.net', // live consumer host until the Azure cutover
  'app.neighborli.net',
]);

// Consent gate — GDPR: no analytics until the visitor accepts cookies.
// See services/cookieConsent for storage + event details.
import { hasConsent as _hasConsent, CONSENT_CHANGED_EVENT as _CONSENT_EVENT } from './cookieConsent';

let initialized = false;

/** Environment gate. Everything else short-circuits when this returns false. */
export function isGAEnabled() {
  if (typeof window === 'undefined') return false;
  if (!PROD_HOSTNAMES.has(window.location.hostname)) return false;
  // GDPR: never fire until the visitor accepts cookies.
  return _hasConsent();
}

/**
 * djb2-style 32-bit hash → hex string. Not cryptographic, but sufficient
 * to render user IDs opaque before shipping them to Google. Keeps analytics
 * user segmentation possible without exposing real UUIDs on the wire.
 */
function hashId(raw) {
  if (!raw) return '';
  let hash = 5381;
  for (let i = 0; i < raw.length; i++) {
    hash = ((hash << 5) + hash) + raw.charCodeAt(i);
    hash |= 0; // 32-bit
  }
  return `u_${(hash >>> 0).toString(16)}`;
}

/** Load the gtag script tag on demand. Idempotent. Also re-runs when the
 * visitor accepts cookies after page load, so we start tracking as soon as
 * consent lands (no reload needed). */
export function initGA() {
  if (initialized || !isGAEnabled()) {
    // Not yet allowed — but subscribe once to consent changes so we can
    // boot the tracker the moment the visitor accepts.
    if (!initialized && typeof window !== 'undefined' && !window.__gaConsentBound) {
      window.__gaConsentBound = true;
      window.addEventListener(_CONSENT_EVENT, () => {
        if (!initialized) initGA();
      });
    }
    return;
  }
  initialized = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  // We call trackPageView() explicitly on each route change, so disable
  // gtag's own auto page_view (it only fires on the initial load).
  gtag('config', MEASUREMENT_ID, { send_page_view: false });
}

/** Attach a user_id to subsequent hits. Call after login. */
export function identify(userId) {
  if (!isGAEnabled() || !window.gtag) return;
  window.gtag('config', MEASUREMENT_ID, {
    user_id: hashId(userId),
    send_page_view: false,
  });
}

/** Clear the user_id after logout. */
export function clearIdentity() {
  if (!isGAEnabled() || !window.gtag) return;
  window.gtag('config', MEASUREMENT_ID, {
    user_id: null,
    send_page_view: false,
  });
}

/** Manual page_view hit for React Router transitions. */
export function trackPageView(path, title) {
  if (!isGAEnabled() || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
}

/**
 * Generic event tracker. `name` is the GA4 event name (snake_case). `params`
 * gets merged in as event parameters. Prefer stable, low-cardinality values.
 */
export function track(name, params = {}) {
  if (!isGAEnabled() || !window.gtag) return;
  window.gtag('event', name, params);
}
